import { useMemo, useState } from "react";
import { MODULE_CATEGORIES } from "@/types/modules";

interface ModuleListPanelProps {
  modules: any[];
  selectedModule: string | null;
  onSelectModule: (mod: any) => void;
  onNewModule?: () => void;
  getModuleIcon: (icon: string | null | undefined) => string;
}

export function ModuleListPanel({
  modules,
  selectedModule,
  onSelectModule,
  onNewModule,
  getModuleIcon,
}: ModuleListPanelProps) {
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = modules.filter((mod) => {
      if (!term) return true;
      return (
        mod.code.toLowerCase().includes(term) ||
        mod.name.toLowerCase().includes(term)
      );
    });
    const groups: Record<string, any[]> = {};
    for (const mod of filtered) {
      const cat = mod.category || "OTHER";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(mod);
    }
    // Sort modules within each group by sortOrder
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return groups;
  }, [modules, search]);

  return (
    <div className="flex flex-col h-full border-r border-slate-200">
      <div className="p-3 border-b border-slate-100 space-y-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search modules..."
          className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm"
        />
        {onNewModule ? (
          <button
            type="button"
            onClick={onNewModule}
            className="w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white"
          >
            + New Module
          </button>
        ) : (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-4 text-slate-500">
            New module codes are added in the shared catalog and a database
            migration.
          </p>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {Object.keys(grouped).length === 0 ? (
          <p className="p-4 text-xs text-slate-400">No modules found.</p>
        ) : (
          Object.entries(grouped).map(([category, mods]) => {
            const catMeta =
              MODULE_CATEGORIES[category as keyof typeof MODULE_CATEGORIES];
            return (
              <div key={category}>
                <div className="sticky top-0 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                  {catMeta?.label || category} ({mods.length})
                </div>
                {mods.map((mod) => {
                  const isSelected = selectedModule === mod.code;
                  return (
                    <button
                      key={mod.id}
                      type="button"
                      onClick={() => onSelectModule(mod)}
                      className={`w-full text-left px-3 py-2.5 border-b border-slate-50 transition-colors ${
                        isSelected
                          ? "bg-teal-50 border-l-2 border-l-teal-500"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          {getModuleIcon(mod.icon)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {mod.name}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-slate-400 font-mono">
                              {mod.code}
                            </span>
                            {mod.isActive ? (
                              <span className="text-[10px] text-emerald-600">
                                Active
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-400">
                                Inactive
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-300">
                          #{mod.sortOrder}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
